import { TriangleExclamation } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ArtEmpty, type ArtTone } from './Art.tsx';

/**
 * Keeps one part of a page from taking the whole page with it. A part that throws while rendering
 * (0.33.0's History part did, on an empty list) shows an empty state in its place, with a way to try
 * again, while the header, the tabs and every other part keep working. The key resets it: give the
 * part's name, so moving to another part clears a failure.
 */
export class PartBoundary extends Component<{ name: string; tone: ArtTone; children: ReactNode }, { failed?: string }> {
  override state: { failed?: string } = {};

  static getDerivedStateFromError(error: unknown): { failed: string } {
    return { failed: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(`The ${this.props.name} part could not be shown`, error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.failed === undefined) return this.props.children;
    return (
      <div className="p-4">
        <ArtEmpty
          tone={this.props.tone}
          icon={<TriangleExclamation />}
          compact
          title={`${this.props.name} could not be shown`}
          action={
            <Button size="sm" variant="secondary" onPress={() => this.setState({ failed: undefined })}>
              Try again
            </Button>
          }
        >
          Something in this part went wrong while drawing it. The rest of the page is unaffected; if it happens again, the error log has the details.
        </ArtEmpty>
      </div>
    );
  }
}
